import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';

import { useIncubatorSelection } from '../hooks/useIncubatorSelection';

export type IncubatorSelectionFieldsProps = {
  selection: ReturnType<typeof useIncubatorSelection>;
  labelCount: number;
};

export const IncubatorSelectionFields = ({
  selection,
  labelCount,
}: IncubatorSelectionFieldsProps) => {
  const { t } = useTranslation();
  const {
    incubatorTypes,
    counts,
    setCount,
    orderedActiveTypes,
    isEven,
    toggleEven,
    moveType,
    preferenceDisabled,
    totalCapacity,
    hasCapacityError,
    planValidationError,
  } = selection;

  return (
    <>
      <div className="mb-2 flex flex-col gap-2">
        {incubatorTypes.map((it) => (
          <div
            key={it.name}
            className="flex items-center justify-between gap-2"
          >
            <label className="text-sm text-gray-700">{it.name}</label>
            <Input
              type="number"
              min={0}
              value={counts[it.name] || 0}
              onChange={(e) => setCount(it.name, parseInt(e.target.value) || 0)}
              onClick={(e) => e.stopPropagation()}
              className="w-16 text-center"
            />
          </div>
        ))}
      </div>
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between text-xs font-medium text-gray-600">
          <span>{t('fertWork.preference', 'Preference')}</span>
          <button
            type="button"
            disabled={preferenceDisabled}
            onClick={(e) => {
              e.stopPropagation();
              toggleEven();
            }}
            className={`rounded border px-1.5 py-0.5 text-xs ${
              isEven
                ? 'border-cyan-600 bg-cyan-600 text-white'
                : 'border-gray-300 bg-white text-gray-600'
            } ${preferenceDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          >
            {t('fertWork.even', 'Even')}
          </button>
        </div>
        {!isEven && (
          <div className="flex flex-col gap-1">
            {orderedActiveTypes.length === 0 && (
              <span className="text-xs text-gray-400">
                {t(
                  'fertWork.selectIncubatorFirst',
                  'Enter a count above to set fill order',
                )}
              </span>
            )}
            {orderedActiveTypes.map((name, index) => (
              <div
                key={name}
                className="flex items-center justify-between gap-2 rounded border border-gray-200 px-2 py-1 text-xs"
              >
                <span>
                  {index + 1}. {name}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="border-none bg-transparent text-gray-500 disabled:opacity-30"
                    disabled={index === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveType(name, -1);
                    }}
                  >
                    <i className="pi pi-arrow-up" />
                  </button>
                  <button
                    type="button"
                    className="border-none bg-transparent text-gray-500 disabled:opacity-30"
                    disabled={index === orderedActiveTypes.length - 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveType(name, 1);
                    }}
                  >
                    <i className="pi pi-arrow-down" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {hasCapacityError && (
        <div className="mb-2 flex items-start gap-1.5 rounded bg-red-50 px-2 py-1.5 text-xs text-red-700">
          <i className="pi pi-exclamation-triangle mt-0.5 flex-shrink-0" />
          <span>
            {planValidationError ??
              t(
                'fertWork.notEnoughSpace',
                'Not enough space. {labelCount} labels need at least {labelCount} slots ({totalCapacity} available).',
                { labelCount, totalCapacity },
              )}
          </span>
        </div>
      )}
    </>
  );
};
